import { FromBufferstockActionBase } from '../from-bufferstock-action.base';
import {
  BookingParams,
  BookingPosition,
  BookingResult,
  StockTargetType,
} from '../../types';

/**
 * Bufferstock-Quelle in der Unified-Welt. Zusätzlich zu den geteilten
 * Bufferstock-Richtungen bietet sie die welt-übergreifenden Ziele KFZ
 * (CarTransfer) und Auftrag.
 */
export class UnifiedFromBufferstockAction extends FromBufferstockActionBase {
  /**
   * Routet die Buchung nach `targetType` auf die geteilten
   * Bufferstock-Richtungen plus die welt-übergreifenden Ziele KFZ und Auftrag.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult der gewählten Richtung bzw. Fehler bei unzulässigem Ziel
   */
  public async execute(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    this.debugInfo('[from-bufferstock:unified]', position, params);
    switch (params.context.booking.targetType) {
      case StockTargetType.BUFFERSTOCK:
        return this.toBufferstock(position, params);
      case StockTargetType.CAR:
        return this.toCar(position, params);
      case StockTargetType.MACHINE:
        return this.toMachine(position, params);
      case StockTargetType.TRASH_BIN:
        return this.toTrashBin(position, params);
      case StockTargetType.CONSUMPTION_BIN:
        return this.toConsumptionBin(position, params);
      case StockTargetType.BUFFERSTOCK_DISPOSAL:
        return this.failure(
          'Bufferstock → BufferstockDisposal ist keine valide Direction',
        );
      default:
        return this.exhaustiveFailure(params.context.booking.targetType);
    }
  }

  /**
   * Bufferstock → KFZ: Befüllung des KFZ aus dem Bufferstock.
   * Erzeugt ein CarTransferMovement.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler bei fehlender targetId, fehlendem Fahrzeuglager, amount <= 0 oder Speicher-/Update-Fehlschlag
   */
  protected async toCar(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { context, adapters } = params;
    const { booking } = context;

    if (booking.targetId === undefined) {
      return this.failure('Bufferstock->KFZ: targetId (KFZ) fehlt');
    }
    // Empfangendes Fahrzeug muss auf diesem Gerät geladen sein.
    if (!adapters.car.hasLocalInventory()) {
      return this.failure(
        'Bufferstock->KFZ: kein lokales Fahrzeuglager vorhanden',
      );
    }
    if (position.amount <= 0) {
      return this.failure('Bufferstock->KFZ: amount muss > 0 sein');
    }
    // 1. Warehouse: CarTransferMovement in die Offline-Queue (einphasig).
    const movement = adapters.movement.build(
      position,
      booking,
      params.clientIds.idFor(position),
    );
    const queued = await adapters.movement.enqueue(movement);
    if (!queued.value) {
      return this.failure(
        'CarTransferMovement konnte nicht in der Queue gespeichert werden',
        queued.retryable,
      );
    }
    // 2. Bufferstock lokal: Quelle verringern. Optimistisch (Backend bucht beim
    //    Drainen ebenfalls, Down-Sync gleicht ab) UND nötig, damit der Overbook-
    //    Check bei Folgebuchungen den richtigen Bestand sieht.
    const bufferstockUpdated =
      await adapters.bufferstock.updatePositionAmountLocal(
        position,
        booking.sourceId,
        -position.amount,
      );
    if (!bufferstockUpdated) {
      return this.failure(
        `Bufferstock ${booking.sourceId} konnte nicht aktualisiert werden`,
      );
    }
    // 3. Car-Leg lokal: Ziel erhöhen.
    const carUpdated = await adapters.car.updatePositionAmountLocal(
      position,
      +position.amount,
    );
    if (!carUpdated) {
      return this.failure('Car-Bestand konnte nicht aktualisiert werden');
    }
    return this.success();
  }
}
